import { useMemo } from "react";

/**
 * Single source of truth for how CLAÈ aftercare content is rendered.
 *
 * The Anti-Wrinkle Aftercare form defines the visual template — every other
 * form is rendered through this same component so the typography, spacing,
 * headings, bullets and rhythm stay identical across all treatments.
 *
 * Content-shaping rules (never rewrite the admin's wording):
 * - blank line               -> paragraph break
 * - line starting bullet     -> bullet item (grouped into a <ul>)
 * - line that is ALL-CAPS    -> small tracked eyebrow-heading
 * - everything else          -> paragraph
 *
 * The admin can therefore paste plain text and this component will emit the
 * exact same Anti-Wrinkle-style structure without touching the words.
 */

// Bullet-marker characters. The admin's source may use any of these; we
// normalise them all to a `<li>` unless the line looks like a heading.
const BULLET_RE = /^[•*\-●⚫◦▪◆]\s?/;

// Detect a line the admin intends as a section heading (e.g.
// "BOTOX/ANTI-WRINKLE INJECTIONS AFTERCARE"). Rule: at least 4 chars, no
// bullet prefix, and ≥80% of the letters are uppercase.
function isAllCapsHeading(s) {
  if (!s || s.length < 4) return false;
  if (BULLET_RE.test(s)) return false;
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length < 3) return false;
  const upper = letters.replace(/[^A-Z]/g, "").length;
  return upper / letters.length >= 0.8;
}

// Detect a Title-Case section heading. Colon-terminated lines are allowed
// (e.g. "Immediately After Treatment:"), and commas are allowed only when
// the line is colon-terminated (e.g. "Mild Redness, Swelling, and Bruising:").
const TITLE_LINE_MAX = 80;
const TITLE_WORD_MAX = 12;
function isTitleCaseHeading(s) {
  if (!s || s.length < 3 || s.length > TITLE_LINE_MAX) return false;
  if (!/^[A-Za-z]/.test(s)) return false;
  if (!/^[A-Z]/.test(s)) return false;
  if (/[.!?;]$/.test(s)) return false;
  if (s.includes(",") && !s.endsWith(":")) return false;
  if (BULLET_RE.test(s)) return false;
  const words = s.split(/\s+/);
  if (words.length > TITLE_WORD_MAX) return false;
  const significant = words.filter((w) => w.replace(/[^A-Za-z]/g, "").length >= 4);
  if (significant.length === 0) return words.length <= 4;
  const upper = significant.filter((w) => /^[A-Z]/.test(w)).length;
  return upper / significant.length >= 0.6;
}

// Numbered headings like "1. Introduction", "2. Understanding Dermal Fillers",
// "3) Expected Results". We keep the number prefix in the rendered text so the
// admin's wording is preserved verbatim.
function isNumberedHeading(s) {
  const m = s.match(/^\d+[.)]\s+(.+)$/);
  if (!m) return false;
  const rest = m[1].trim();
  if (isTitleCaseHeading(rest)) return true;
  // Fallback: short line starting uppercase, no terminal sentence punctuation
  return rest.length <= 60 && /^[A-Z]/.test(rest) && !/[.!?]$/.test(rest);
}

export default function AftercareRenderer({ text, className = "" }) {
  const blocks = useMemo(() => {
    const lines = (text || "").split("\n").map((l) => l.replace(/\s+$/, ""));
    const out = [];
    let para = [];
    let bullets = [];
    const flushPara = () => {
      if (para.length) { out.push({ type: "p", text: para.join(" ").trim() }); para = []; }
    };
    const flushBullets = () => {
      if (bullets.length) { out.push({ type: "ul", items: [...bullets] }); bullets = []; }
    };
    for (const ln of lines) {
      const s = ln.trim();
      if (!s) { flushPara(); flushBullets(); continue; }
      // Bullet-marker line — check if it's actually a bullet-styled heading
      // (like "● Mild Discomfort:" or "● Cold Compress"): short Title-Case
      // phrase after stripping the marker → render as h-section, otherwise
      // it's a real bullet item.
      if (BULLET_RE.test(s)) {
        const inner = s.replace(BULLET_RE, "").trim();
        const withoutColon = inner.replace(/:$/, "").trim();
        const looksLikeHeading =
          isTitleCaseHeading(withoutColon) ||
          (inner.endsWith(":") && withoutColon.length <= 80 && /^[A-Z]/.test(withoutColon));
        if (looksLikeHeading) {
          flushPara(); flushBullets();
          out.push({ type: "h-section", text: withoutColon });
          continue;
        }
        // Inline "● Heading: body text on same line" pattern (Profhilo). If
        // the pre-colon segment is a Title-Case phrase, split it into a
        // heading + paragraph pair. Every character is preserved — the colon
        // simply stays with the heading.
        const colonIdx = inner.indexOf(":");
        if (colonIdx > 0 && colonIdx < 60) {
          const headText = inner.slice(0, colonIdx).trim();
          const bodyText = inner.slice(colonIdx + 1).trim();
          if (isTitleCaseHeading(headText) && bodyText.length > 0) {
            flushPara(); flushBullets();
            out.push({ type: "h-section", text: headText });
            para.push(bodyText);
            continue;
          }
        }
        flushPara();
        bullets.push(inner);
        continue;
      }
      if (isAllCapsHeading(s)) {
        flushPara(); flushBullets();
        out.push({ type: "h-eyebrow", text: s });
        continue;
      }
      if (isNumberedHeading(s)) {
        flushPara(); flushBullets();
        out.push({ type: "h-section", text: s });
        continue;
      }
      if (isTitleCaseHeading(s)) {
        flushPara(); flushBullets();
        // Strip trailing colon from the visible heading text — it looks
        // cleaner and matches the Anti-Wrinkle template.
        out.push({ type: "h-section", text: s.replace(/:$/, "") });
        continue;
      }
      flushBullets();
      para.push(s);
    }
    flushPara(); flushBullets();
    return out;
  }, [text]);

  if (!text?.trim()) {
    return (
      <p
        className={`text-[13px] italic text-[#66615C] ${className}`}
        data-testid="aftercare-empty"
      >
        No aftercare information has been recorded yet.
      </p>
    );
  }

  return (
    <div
      className={`space-y-4 text-[14px] leading-[1.75] text-[#141414] ${className}`}
      data-testid="aftercare-rendered"
      style={{ fontFamily: "'Plus Jakarta Sans', Arial, sans-serif" }}
    >
      {blocks.map((b, i) => {
        if (b.type === "h-eyebrow") {
          return (
            <p
              key={i}
              className="mono-label text-[11px] tracking-[0.24em] uppercase font-semibold text-[#141414] pt-3"
              data-testid="aftercare-subheading"
            >
              {b.text}
            </p>
          );
        }
        if (b.type === "h-section") {
          return (
            <p
              key={i}
              className="font-semibold text-[16px] sm:text-[17px] leading-tight text-[#141414] pt-3 pb-0"
              data-testid="aftercare-section-heading"
            >
              {b.text}
            </p>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5">
              {b.items.map((it, j) => <li key={j}>{it}</li>)}
            </ul>
          );
        }
        return <p key={i}>{b.text}</p>;
      })}
    </div>
  );
}
